import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';

@Component({
  tag: 'sy-progress-spinner',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class ProgressSpinner {
  private transition = 'none';
  private strokeWidth = 10;

  @Element() el: HTMLSyProgressSpinnerElement;

  /**
   * Defines if the progress spinner will have a background circle.
   */
  @Prop({ reflect: true }) hasBackground = false;

  /**
   * Percentage of the circle that must be active. Must be a value between 0 and 100. Only applied if the 'type' is 'determinate'.
   */
  @Prop({ mutable: true }) value: number;

  /**
   * Defines loading size.
   */
  @Prop({ reflect: true }) size: 'small' | 'medium' | 'large' = 'small';

  /**
   * State of the progress spinner: 'determinate' means that the percentage of completion is known and the circle filled portion will be defined by 'value',
   * 'indeterminate' means that the percentage is unknown and the circle will play a looping animation.
   */
  @Prop({ reflect: true }) type: 'determinate' | 'indeterminate' = 'determinate';

  @Watch('value')
  validateValueWatcher(): void {
    // Adjusts invalid values and sets a fixed value if type is indeterminate
    this.fixValue();
  }

  componentWillLoad() {
    this.fixValue();
  }

  componentWillRender() {
    this.getStrokeWidth();
  }

  componentDidRender() {
    this.unlockTransition();
  }

  private fixValue() {
    if (this.value < 0) {
      this.value = 0;
    } else if (this.value > 100) {
      this.value = 100;
    }
  }

  private getInnerRadius() {
    return (100 - this.strokeWidth) / 2;
  }

  private getStrokeWidth() {
    this.strokeWidth = Number(getComputedStyle(this.el).getPropertyValue('--sy-progress-spinner-stroke-width')) || 10;

    return this.strokeWidth;
  }

  private unlockTransition() {
    this.transition = this.type === 'determinate' ? 'stroke-dashoffset .6s ease' : 'none';
  }

  private getCSSTokens() {
    return {
      '--sy-progress-spinner-value': this.type === 'indeterminate' ? 40 : this.value,
      '--sy-progress-spinner-stroke-width-percentage': `${this.strokeWidth}%`,
    } as any;
  }

  private getCircleStyle() {
    const style: any = {
      transition: this.transition,
    };
    return style;
  }

  render() {
    return (
      <Host style={this.getCSSTokens()}>
        <svg>
          {this.hasBackground ? (
            <circle class="progress-spinner-background" cx="50%" cy="50%" r={`${this.getInnerRadius()}%`} />
          ) : (
            ''
          )}
          <circle
            class="progress-spinner-circle"
            cx="50%"
            cy="50%"
            r={`${this.getInnerRadius()}%`}
            style={this.getCircleStyle()}
          />
        </svg>
      </Host>
    );
  }
}
